import React, { useState, useEffect, useMemo } from 'react';
import { useDiffusions } from '../hooks/useDiffusions';
import { useBars } from '../hooks/useBars';
import { DiffusionRow } from '../components/DiffusionRow';
import { logAction, getCurrentAdmin } from '../services/logger';
import { supabase } from '../lib/supabase';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';

export function DiffusionsPage() {
  const [dateRange, setDateRange] = useState<'today' | 'tomorrow' | 'week' | 'all'>('today');
  const [status, setStatus] = useState('');
  const [barId, setBarId] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addBarId, setAddBarId] = useState('');
  const [addMatchId, setAddMatchId] = useState('');
  const [addStatus, setAddStatus] = useState('unconfirmed');
  const [todayMatches, setTodayMatches] = useState<any[]>([]);

  const filters = useMemo(() => ({
    dateRange,
    status: status || undefined,
    barId: barId || undefined,
  }), [dateRange, status, barId]);

  const { diffusions, loading, refetch, updateStatus, createDiffusion } = useDiffusions(filters);
  const { bars } = useBars();

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('diffusions-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'diffusions' }, () => {
        refetch();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [refetch]);

  // Load matches for add form
  useEffect(() => {
    const loadMatches = async () => {
      const today = new Date().toISOString().split('T')[0];
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const { data } = await supabase
        .from('matches')
        .select('id, home_team, away_team, match_time, match_date')
        .gte('match_date', today)
        .lte('match_date', tomorrow.toISOString().split('T')[0])
        .order('match_date')
        .order('match_time');
      setTodayMatches(data || []);
    };
    loadMatches();
  }, []);

  const handleStatusChange = async (id: string, newStatus: string, oldStatus: string, barName: string, matchName: string) => {
    if (newStatus === oldStatus) return;
    const admin = getCurrentAdmin();
    await updateStatus(id, newStatus);
    const statusLabels: Record<string, string> = { unconfirmed: 'non vérifié', probable: 'probable', confirmed: 'confirmé' };
    await logAction('confirm_diffusion', 'diffusion', id,
      { status: oldStatus }, { status: newStatus },
      `${admin?.name} a passé la diffusion de ${matchName} au ${barName} en "${statusLabels[newStatus]}"`
    );
    toast.success('Statut mis à jour');
  };

  const handleAddDiffusion = async () => {
    if (!addBarId || !addMatchId) { toast.error('Sélectionne un bar et un match'); return; }
    const admin = getCurrentAdmin();
    const bar = bars.find(b => b.id === addBarId);
    const match = todayMatches.find((m: any) => m.id === addMatchId);
    await createDiffusion(addBarId, addMatchId, addStatus);
    await logAction('confirm_diffusion', 'diffusion', null, null,
      { bar: bar?.name, match: `${match?.home_team} vs ${match?.away_team}`, status: addStatus },
      `${admin?.name} a ajouté une diffusion de ${match?.home_team} vs ${match?.away_team} au ${bar?.name}`
    );
    toast.success('Diffusion ajoutée');
    setShowAdd(false);
    setAddBarId('');
    setAddMatchId('');
  };

  return (
    <div>
      <div className="filters-row">
        <select className="filter-select" value={dateRange} onChange={e => setDateRange(e.target.value as any)}>
          <option value="today">Aujourd'hui</option>
          <option value="tomorrow">Demain</option>
          <option value="week">Cette semaine</option>
          <option value="all">Tout</option>
        </select>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Tous statuts</option>
          <option value="unconfirmed">Non vérifié</option>
          <option value="probable">Probable</option>
          <option value="confirmed">Confirmé</option>
        </select>
        <select className="filter-select" value={barId} onChange={e => setBarId(e.target.value)}>
          <option value="">Tous les bars</option>
          {bars.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
          <Plus size={16} /> Ajouter diffusion
        </button>
        <span style={{ fontSize: '12px', color: 'var(--text-dim)', marginLeft: 'auto' }}>
          {diffusions.length} résultats
        </span>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label>Bar</label>
              <select value={addBarId} onChange={e => setAddBarId(e.target.value)} className="filter-select" style={{ width: '100%' }}>
                <option value="">Choisir...</option>
                {bars.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label>Match</label>
              <select value={addMatchId} onChange={e => setAddMatchId(e.target.value)} className="filter-select" style={{ width: '100%' }}>
                <option value="">Choisir...</option>
                {todayMatches.map((m: any) => <option key={m.id} value={m.id}>{m.home_team} vs {m.away_team} ({m.match_time})</option>)}
              </select>
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label>Statut initial</label>
              <select value={addStatus} onChange={e => setAddStatus(e.target.value)} className="filter-select" style={{ width: '100%' }}>
                <option value="unconfirmed">Non vérifié</option>
                <option value="probable">Probable</option>
                <option value="confirmed">Confirmé</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleAddDiffusion}>Ajouter</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="skeleton-list">
            {[1,2,3,4,5].map(i => <div key={i} className="skeleton-row" />)}
          </div>
        ) : diffusions.length === 0 ? (
          <p className="muted" style={{ padding: '20px' }}>Aucune diffusion trouvée</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Bar</th>
                <th>Match</th>
                <th>Date/Heure</th>
                <th>Statut</th>
                <th>Confirmé par</th>
                <th>Date confirm.</th>
              </tr>
            </thead>
            <tbody>
              {diffusions.map(d => (
                <DiffusionRow key={d.id} diffusion={d} onStatusChange={handleStatusChange} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
